import { ChevronDown, Filter, MapPin, Search, SlidersHorizontal, Tag, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  DrawingCategory,
  DrawingDiscipline,
  DrawingPhase,
  DrawingQuery,
  DrawingStatus,
} from '../../types';
import { DatePicker, Dropdown, Input } from '../forms';

interface DrawingFiltersProps {
  query: DrawingQuery;
  onQueryChange: (query: DrawingQuery) => void;
  projects?: Array<{ value: string; label: string }>;
  categories?: Array<{ value: string; label: string; color?: string }>;
  statuses?: Array<{ value: string; label: string; color?: string }>;
  disciplines?: Array<{ value: string; label: string }>;
  phases?: Array<{ value: string; label: string }>;
  sortOptions?: Array<{ value: string; label: string }>;
  showProject?: boolean;
  loading?: boolean;
}

const DrawingFilters: React.FC<DrawingFiltersProps> = ({
  query,
  onQueryChange,
  projects = [],
  categories = [],
  statuses = [],
  disciplines = [],
  phases = [],
  sortOptions = [],
  showProject = false,
  loading = false,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchInput, setSearchInput] = useState(query.search || '');
  const [tagInput, setTagInput] = useState('');

  // Update search input when query changes externally
  useEffect(() => {
    setSearchInput(query.search || '');
  }, [query.search]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== query.search) {
        onQueryChange({ ...query, search: searchInput || undefined, page: 1 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, query, onQueryChange]);

  // Quick filters state
  const [activeFilters, setActiveFilters] = useState<{
    category?: DrawingCategory;
    status?: DrawingStatus;
    discipline?: DrawingDiscipline;
    phase?: DrawingPhase;
  }>({
    category: query.category,
    status: query.status,
    discipline: query.discipline,
    phase: query.phase,
  });

  // Update query when filters change
  const updateQuery = (updates: Partial<DrawingQuery>) => {
    onQueryChange({ ...query, ...updates, page: 1 });
  };

  // Handle quick filter clicks
  const handleQuickFilter = (filterType: keyof typeof activeFilters, value: string) => {
    const newFilters = { ...activeFilters };

    if (newFilters[filterType] === value) {
      // Remove filter if already active
      delete newFilters[filterType];
      updateQuery({ [filterType]: undefined });
    } else {
      // Set new filter
      newFilters[filterType] = value as any;
      updateQuery({ [filterType]: value });
    }

    setActiveFilters(newFilters);
  };

  // Handle tag addition
  const addTag = () => {
    if (tagInput.trim()) {
      const currentTags = query.tags || [];
      if (!currentTags.includes(tagInput.trim())) {
        updateQuery({ tags: [...currentTags, tagInput.trim()] });
      }
      setTagInput('');
    }
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Remove tag
  const removeTag = (tagToRemove: string) => {
    const currentTags = query.tags || [];
    updateQuery({ tags: currentTags.filter(tag => tag !== tagToRemove) });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchInput('');
    setTagInput('');
    setActiveFilters({});
    onQueryChange({
      page: 1,
      limit: query.limit,
      sort_by: query.sort_by,
      sort_order: query.sort_order,
    });
  };

  // Count active filters
  const activeFilterCount =
    Object.values(activeFilters).filter(Boolean).length +
    (query.search ? 1 : 0) +
    (query.tags?.length || 0) +
    (query.uploaded_by ? 1 : 0) +
    (query.date_from || query.date_to ? 1 : 0) +
    (query.location ? 1 : 0);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
      {/* Search and Sort Row */}
      <div className="flex items-center space-x-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search drawings by name, description, drawing number..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-10"
            disabled={loading}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="w-48">
          <Dropdown
            placeholder="Sort by..."
            options={sortOptions}
            value={query.sort_by || ''}
            onChange={value => updateQuery({ sort_by: value as any })}
            disabled={loading}
          />
        </div>

        {/* Sort Order */}
        <button
          onClick={() =>
            updateQuery({
              sort_order: query.sort_order === 'asc' ? 'desc' : 'asc',
            })
          }
          className={`px-3 py-2 rounded-lg border transition-colors ${
            query.sort_order === 'desc'
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
          }`}
          disabled={loading}
        >
          {query.sort_order === 'desc' ? '↓' : '↑'}
        </button>

        {/* Advanced Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center px-3 py-2 rounded-lg border transition-colors ${
            showAdvanced || activeFilterCount > 0
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
          }`}
          disabled={loading}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 ml-2 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2">
        {/* Project Filter */}
        {showProject && projects.length > 0 && (
          <div className="w-48">
            <Dropdown
              placeholder="All Projects"
              options={[{ value: '', label: 'All Projects' }, ...projects]}
              value={query.project_id || ''}
              onChange={value => updateQuery({ project_id: value || undefined })}
              disabled={loading}
            />
          </div>
        )}

        {/* Category Quick Filters */}
        {categories.slice(0, 4).map(category => (
          <button
            key={category.value}
            onClick={() => handleQuickFilter('category', category.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              activeFilters.category === category.value
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
            }`}
            disabled={loading}
          >
            {category.label}
          </button>
        ))}

        {/* Status Quick Filters */}
        {statuses.slice(0, 3).map(status => (
          <button
            key={status.value}
            onClick={() => handleQuickFilter('status', status.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              activeFilters.status === status.value
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
            }`}
            disabled={loading}
          >
            {status.label}
          </button>
        ))}

        {/* Clear filters button */}
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            disabled={loading}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="border-t border-gray-200 pt-4 space-y-4">
          <div className="flex items-center text-sm font-medium text-gray-700 mb-3">
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Advanced Filters
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Category */}
            <Dropdown
              label="Category"
              placeholder="All Categories"
              options={[{ value: '', label: 'All Categories' }, ...categories]}
              value={query.category || ''}
              onChange={value => updateQuery({ category: (value as DrawingCategory) || undefined })}
              disabled={loading}
            />

            {/* Status */}
            <Dropdown
              label="Status"
              placeholder="All Statuses"
              options={[{ value: '', label: 'All Statuses' }, ...statuses]}
              value={query.status || ''}
              onChange={value => updateQuery({ status: (value as DrawingStatus) || undefined })}
              disabled={loading}
            />

            {/* Discipline */}
            <Dropdown
              label="Discipline"
              placeholder="All Disciplines"
              options={[{ value: '', label: 'All Disciplines' }, ...disciplines]}
              value={query.discipline || ''}
              onChange={value =>
                updateQuery({ discipline: (value as DrawingDiscipline) || undefined })
              }
              disabled={loading}
            />

            {/* Phase */}
            <Dropdown
              label="Phase"
              placeholder="All Phases"
              options={[{ value: '', label: 'All Phases' }, ...phases]}
              value={query.phase || ''}
              onChange={value => updateQuery({ phase: (value as DrawingPhase) || undefined })}
              disabled={loading}
            />

            {/* Date Range */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker
                  placeholder="From"
                  value={query.date_from || ''}
                  onChange={date => updateQuery({ date_from: date || undefined })}
                  disabled={loading}
                />
                <DatePicker
                  placeholder="To"
                  value={query.date_to || ''}
                  onChange={date => updateQuery({ date_to: date || undefined })}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Location */}
            <div className="relative">
              <MapPin className="absolute left-3 top-9 text-gray-400 h-4 w-4" />
              <Input
                label="Location"
                placeholder="Site, building, floor..."
                value={query.location || ''}
                onChange={e => updateQuery({ location: e.target.value || undefined })}
                className="pl-10"
                disabled={loading}
              />
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Tags</label>

            {/* Add tag input */}
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                disabled={!tagInput.trim() || loading}
              >
                Add
              </button>
            </div>

            {/* Active tags */}
            {query.tags && query.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {query.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingFilters;
